import React, { useState, useEffect, useCallback } from 'react';
import { IcoCheck, IcoX, IcoClose, IcoAlertTriangle, IcoCheckCircle, IcoClipboard, IcoTrash } from '../dashboard/DashIcons';

function SubmissionBadge({ status, reviewNotes, reviewedBy, reviewedAt }) {
  const map = {
    pending:  { bg: '#fef9c3', color: '#92400e', border: '#fde68a', label: '⏳ Pending Approval' },
    approved: { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0', label: 'Approved' },
    rejected: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Rejected — Revise & Resubmit' },
  };
  const s = map[status] || map.pending;
  const hasResubmitNote = status === 'approved' && reviewNotes?.includes('Needs resubmission:');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '0.75rem' }}>
      <span style={{ display: 'inline-flex', alignSelf: 'flex-start', background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: '20px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: '700' }}>
        {s.label}
      </span>
      {status === 'rejected' && reviewNotes && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', maxWidth: '520px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#991b1b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>Rejection Reason</div>
          <div style={{ fontSize: '0.85rem', color: '#7f1d1d', lineHeight: '1.5' }}>{reviewNotes}</div>
          {reviewedBy && (
            <div style={{ fontSize: '0.73rem', color: '#b91c1c', marginTop: '6px' }}>
              — {reviewedBy}{reviewedAt ? ` · ${new Date(reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
          )}
        </div>
      )}
      {hasResubmitNote && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', maxWidth: '520px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#92400e', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>Action Required</div>
          <div style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: '1.5' }}>
            {reviewNotes.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
          {reviewedBy && (
            <div style={{ fontSize: '0.73rem', color: '#92400e', marginTop: '6px' }}>
              — {reviewedBy}{reviewedAt ? ` · ${new Date(reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { SubmissionBadge };

export default function PendingApprovalsPanel({ headers, onClose, organizationId }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const [noteError, setNoteError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [rejectedEmployees, setRejectedEmployees] = useState({});
  const [confirmDeleteSub, setConfirmDeleteSub] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'pending' ? '/api/payroll/pending' : '/api/payroll/submissions';
      const res = await fetch(endpoint, { headers });
      if (res.ok) { const data = await res.json(); setSubmissions(data.submissions || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab, headers]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const toggleEmployeeRejection = (subId, empId) => {
    setRejectedEmployees(prev => {
      const current = new Set(prev[subId] || []);
      const key = String(empId);
      if (current.has(key)) current.delete(key);
      else current.add(key);
      return { ...prev, [subId]: current };
    });
  };

  const handleAction = async (id, action) => {
    const note = reviewNotes[id] || '';
    if (action === 'reject' && !note.trim()) { setNoteError(id); return; }
    setNoteError(null);
    setActionLoading(id + action);
    const rejectedSet = rejectedEmployees[id] || new Set();
    const body = action === 'approve'
      ? { reviewNotes: note, rejectedEmployeeIds: [...rejectedSet] }
      : { reviewNotes: note };
    try {
      const res = await fetch(`/api/payroll/submissions/${id}/${action}`, {
        method: 'PATCH', headers, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        setActionResult({ id, type: 'success', message: data.message || (action === 'approve' ? 'Approved!' : 'Rejected.') });
        setReviewNotes(prev => ({ ...prev, [id]: '' }));
        setRejectedEmployees(prev => { const n = { ...prev }; delete n[id]; return n; });
        setExpandedId(null);
        fetchSubmissions();
      } else {
        setActionResult({ id, type: 'error', message: data.message || 'Action failed.' });
      }
    } catch {
      setActionResult({ id, type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setActionLoading(null);
      setTimeout(() => setActionResult(null), 5000);
    }
  };

  const handleDeleteSubmission = async (id) => {
    setDeleteLoading(id);
    try {
      const res = await fetch(`/api/payroll/submissions/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (res.ok) {
        setConfirmDeleteSub(null);
        setExpandedId(null);
        fetchSubmissions();
      } else {
        setActionResult({ id, type: 'error', message: data.message || 'Delete failed.' });
        setConfirmDeleteSub(null);
        setTimeout(() => setActionResult(null), 5000);
      }
    } catch {
      setActionResult({ id, type: 'error', message: 'Network error. Please try again.' });
      setConfirmDeleteSub(null);
      setTimeout(() => setActionResult(null), 5000);
    } finally {
      setDeleteLoading(null);
    }
  };

  const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const statusBadge = (status) => {
    const map = {
      pending:  { color: '#92400e', bg: '#fef9c3', border: '#fde68a', label: '⏳ Pending' },
      approved: { color: '#065f46', bg: '#ecfdf5', border: '#a7f3d0', label: 'Approved' },
      rejected: { color: '#991b1b', bg: '#fef2f2', border: '#fecaca', label: 'Rejected' }
    };
    const s = map[status] || map.pending;
    return (
      <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: '20px', padding: '3px 10px', fontSize: '0.72rem', fontWeight: '700' }}>
        {s.label}
      </span>
    );
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1800 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '520px', maxWidth: '95vw', background: 'white', zIndex: 1900, boxShadow: '-8px 0 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#111827' }}>Payroll Submissions</h2>
            <p style={{ margin: '3px 0 0', fontSize: '0.76rem', color: '#9ca3af' }}>Review and approve payroll submissions</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><IcoClose size={16}/></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '0 1rem' }}>
          {['pending', 'all'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: tab === t ? '700' : '500', fontSize: '0.85rem', color: tab === t ? '#22c55e' : '#6b7280', borderBottom: tab === t ? '2px solid #22c55e' : '2px solid transparent' }}>
              {t === 'pending' ? 'Pending' : 'All Submissions'}
            </button>
          ))}
        </div>

        {/* Action result banner */}
        {actionResult && (
          <div style={{ margin: '1rem 1rem 0', padding: '10px 14px', borderRadius: '8px', fontSize: '0.84rem', fontWeight: '600', background: actionResult.type === 'success' ? '#ecfdf5' : '#fef2f2', color: actionResult.type === 'success' ? '#065f46' : '#991b1b', border: `1px solid ${actionResult.type === 'success' ? '#a7f3d0' : '#fecaca'}` }}>
            {actionResult.type === 'success' ? <IcoCheck size={14} color="#065f46"/> : <IcoX size={14} color="#991b1b"/>} {actionResult.message}
          </div>
        )}

        {/* Submissions list */}
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading && <p style={{ color: '#9ca3af', textAlign: 'center', padding: '3rem' }}>Loading…</p>}
          {!loading && submissions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#9ca3af' }}>
              <div style={{ marginBottom: '0.75rem' }}>{tab === 'pending' ? <IcoCheckCircle size={40} color="#22c55e"/> : <IcoClipboard size={40} color="#6b7280"/>}</div>
              <p style={{ fontWeight: '600', color: '#6b7280' }}>{tab === 'pending' ? 'No pending submissions!' : 'No submissions yet.'}</p>
            </div>
          )}
          {submissions.map(sub => {
            const isOpen = expandedId === sub._id;
            const snap = sub.payrollSnapshot;
            const note = reviewNotes[sub._id] || '';
            const hasNoteError = noteError === sub._id;
            return (
              <div key={sub._id} style={{ border: `1.5px solid ${sub.status === 'pending' ? '#fde68a' : sub.status === 'approved' ? '#a7f3d0' : '#fecaca'}`, borderRadius: '10px', overflow: 'hidden', background: 'white' }}>

                {/* Row header */}
                <div onClick={() => { setExpandedId(isOpen ? null : sub._id); setNoteError(null); }}
                  style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>Week of {fmt(sub.weekStartDate)}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px' }}>
                      Submitted by {sub.submittedBy?.firstName || sub.submittedBy?.name || sub.submittedBy?.email} · {fmtTime(sub.submittedAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {statusBadge(sub.status)}
                    <span style={{ color: '#d1d5db', fontSize: '0.7rem' }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Snapshot stats */}
                <div style={{ display: 'flex', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                  {[
                    { label: 'Reg Hrs',    val: snap?.totalRegularHours?.toFixed(1),        accent: '#4f46e5' },
                    { label: 'OT Hrs',     val: snap?.totalOvertimeHours?.toFixed(1),       accent: '#f59e0b' },
                    { label: 'PTO Hrs',    val: snap?.totalPTOHours?.toFixed(1),            accent: '#10b981' },
                    { label: 'Lunch Ded.', val: `${snap?.totalLunchDeductedMinutes || 0}m`, accent: '#6b7280' },
                  ].map(({ label, val, accent }) => (
                    <div key={label} style={{ flex: 1, padding: '8px 10px', borderRight: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
                      <div style={{ fontSize: '1rem', fontWeight: '800', color: accent, marginTop: '1px' }}>{val ?? '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6' }}>

                    {/* Employee breakdown table */}
                    {snap?.employeeSummary?.length > 0 && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.4px' }}>Employee Breakdown</div>
                          {sub.status === 'pending' && (
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Toggle to exclude an employee from approval</div>
                          )}
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '14px', fontSize: '0.8rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', padding: '6px 12px', background: '#f5f5f5', fontWeight: '700', color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                            <span>Employee</span><span>Reg</span><span>OT</span><span style={{ color: '#10b981' }}>PTO</span><span>Lunch</span>
                            {sub.status === 'pending' && <span></span>}
                          </div>
                          {snap.employeeSummary.map((emp, i) => {
                            const empId = String(emp.employeeId);
                            const isRejected = (rejectedEmployees[sub._id] || new Set()).has(empId);
                            return (
                              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', padding: '7px 12px', borderTop: '1px solid #f3f4f6', background: isRejected ? '#fef2f2' : (i % 2 ? '#fafafa' : 'white'), opacity: isRejected ? 0.6 : 1 }}>
                                <span style={{ fontWeight: '600', color: isRejected ? '#ef4444' : '#111827', textDecoration: isRejected ? 'line-through' : 'none' }}>{emp.employeeName || '—'}</span>
                                <span style={{ textDecoration: isRejected ? 'line-through' : 'none' }}>{emp.regularHours?.toFixed(1)}</span>
                                <span style={{ color: emp.overtimeHours > 0 ? '#f59e0b' : 'inherit', textDecoration: isRejected ? 'line-through' : 'none' }}>{emp.overtimeHours?.toFixed(1)}</span>
                                <span style={{ color: emp.ptoHours > 0 ? '#10b981' : 'inherit', fontWeight: emp.ptoHours > 0 ? '700' : '400', textDecoration: isRejected ? 'line-through' : 'none' }}>{emp.ptoHours?.toFixed(1)}</span>
                                <span style={{ textDecoration: isRejected ? 'line-through' : 'none' }}>{emp.lunchDeductedMinutes}m</span>
                                {sub.status === 'pending' && (
                                  <button
                                    onClick={() => toggleEmployeeRejection(sub._id, empId)}
                                    title={isRejected ? 'Include this employee' : 'Exclude this employee'}
                                    style={{ background: isRejected ? '#fee2e2' : 'none', border: `1px solid ${isRejected ? '#ef4444' : '#d1d5db'}`, color: isRejected ? '#ef4444' : '#9ca3af', borderRadius: '4px', padding: '2px 7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', lineHeight: 1 }}
                                  >
                                    <IcoClose size={12}/>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {sub.status === 'pending' && (rejectedEmployees[sub._id]?.size || 0) > 0 && (
                          <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '0.8rem', color: '#991b1b', fontWeight: '600' }}>
                            <><IcoAlertTriangle size={13} color="#92400e"/> {rejectedEmployees[sub._id].size} employee{rejectedEmployees[sub._id].size > 1 ? 's' : ''} will be excluded — their time entries will be removed on approval.</>

                          </div>
                        )}
                      </>
                    )}

                    {/* Pending actions */}
                    {sub.status === 'pending' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <textarea
                            placeholder="Review notes (required for rejection, optional for approval)…"
                            value={note}
                            onChange={e => { setReviewNotes(prev => ({ ...prev, [sub._id]: e.target.value })); if (hasNoteError) setNoteError(null); }}
                            rows={2}
                            style={{ width: '100%', border: `1.5px solid ${hasNoteError ? '#ef4444' : '#e0e0e0'}`, borderRadius: '8px', padding: '8px 10px', fontSize: '0.83rem', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                          />
                          {hasNoteError && <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#ef4444', fontWeight: '600' }}>A reason is required before rejecting.</p>}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleAction(sub._id, 'reject')} disabled={!!actionLoading}
                            style={{ padding: '8px 18px', background: 'white', border: '1.5px solid #ef4444', color: '#ef4444', borderRadius: '8px', fontWeight: '700', fontSize: '0.83rem', cursor: 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading === sub._id + 'reject' ? 'Rejecting…' : <><IcoX size={13}/> Reject All</>}
                          </button>
                          <button onClick={() => handleAction(sub._id, 'approve')} disabled={!!actionLoading}
                            style={{ padding: '8px 18px', background: '#059669', border: 'none', color: 'white', borderRadius: '8px', fontWeight: '700', fontSize: '0.83rem', cursor: 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
                            {actionLoading === sub._id + 'approve'
                              ? 'Approving…'
                              : (rejectedEmployees[sub._id]?.size || 0) > 0
                                ? <><IcoCheck size={13}/> Approve ({(snap?.employeeSummary?.length || 0) - rejectedEmployees[sub._id].size} of {snap?.employeeSummary?.length})</>
                                : <><IcoCheck size={13}/> Approve All</>}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Reviewed result */}
                    {sub.status !== 'pending' && (
                      <div style={{ padding: '10px 12px', background: sub.status === 'approved' ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', fontSize: '0.82rem' }}>
                        <div style={{ fontWeight: '700', color: sub.status === 'approved' ? '#065f46' : '#991b1b', marginBottom: sub.reviewNotes ? '6px' : 0 }}>
                          {sub.status === 'approved' ? <><IcoCheck size={14} color="#065f46"/> Approved</> : <><IcoX size={14} color="#991b1b"/> Rejected</>}
                          {sub.reviewedBy && (
                            <span style={{ fontWeight: '400', color: '#6b7280' }}>
                              {' '}· {sub.reviewedBy?.firstName || sub.reviewedBy?.name} · {fmtTime(sub.reviewedAt)}
                            </span>
                          )}
                        </div>
                        {sub.reviewNotes && <p style={{ margin: 0, color: sub.status === 'approved' ? '#15803d' : '#7f1d1d', lineHeight: '1.5' }}>{sub.reviewNotes}</p>}
                      </div>
                    )}

                    {/* Delete submission */}
                    {confirmDeleteSub === sub._id ? (
                      <div style={{ marginTop: '12px', padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '0.83rem' }}>
                        <p style={{ margin: '0 0 10px', color: '#991b1b', fontWeight: '600' }}>
                          Delete this submission{sub.status === 'pending' ? ' and its time entries' : ''}? This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button onClick={() => setConfirmDeleteSub(null)} style={{ padding: '6px 14px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.82rem', color: '#374151' }}>
                            Cancel
                          </button>
                          <button onClick={() => handleDeleteSubmission(sub._id)} disabled={deleteLoading === sub._id}
                            style={{ padding: '6px 14px', background: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '0.82rem', color: 'white', display: 'flex', alignItems: 'center', gap: '5px', opacity: deleteLoading === sub._id ? 0.6 : 1 }}>
                            <IcoTrash size={12}/> {deleteLoading === sub._id ? 'Deleting…' : 'Yes, Delete'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => setConfirmDeleteSub(sub._id)}
                          style={{ padding: '5px 12px', background: 'none', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <IcoTrash size={12}/> Delete Submission
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}